/**
 * O DESCRITIVO, AS ETIQUETAS E OS ESTILOS DA JANELA DE GERAR — T24 (13/10/2026).
 *
 * O operador: «em vez de gerar inicialmente a imagem, ele deveria gerar para mim um descritivo (...)
 * e aí eu posso editar esse descritivo»; «criar uma tag nova ali e salvar essa tag, essa tag vira uma
 * tag global do HQLab»; e «o estilo massinha nosso é isso» — o que cada estilo É vem do painel.
 */
import { auth } from "./firebase.class";
import Firebase from "./firebase.class";
import { conferirTextos } from "./moderar-texto";
import { ESTILOS_DA_TELA } from "./estilos";

const SERVICO = process.env.REACT_APP_BACKEND_ROUTE;

export interface EstiloComEspecificacao {
  tag: string;
  rotulo: string;
  /** O que vai ao desenhista quando é personagem — o painel define. */
  personagem: string;
  cenario: string;
}

/** Os estilos e o que cada um é de fato. Sem o servidor, a lista da tela, sem especificação. */
export async function listarEstilos(): Promise<EstiloComEspecificacao[]> {
  try {
    const r = await fetch(`${SERVICO}/estilos`);
    const j = await r.json();
    if (Array.isArray(j?.estilos) && j.estilos.length) return j.estilos;
  } catch {
    /* sem rede: segue a lista da tela */
  }
  return ESTILOS_DA_TELA.map((e) => ({ ...e, personagem: "", cenario: "" }));
}

export class DescritivoRecusadoError extends Error {}

/** T25 · um campo do descritivo de personagem, como a segunda tela o edita. */
export interface CampoDoDescritivo {
  chave: string;
  rotulo: string;
  valor: string;
}

/** Linhas "Rótulo: valor" viram campos — para o servidor que ainda não manda `campos`. */
export function linhasParaCampos(texto: string): CampoDoDescritivo[] {
  return texto
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l, i) => {
      const k = l.indexOf(":");
      const rotulo = k > 0 ? l.slice(0, k).trim() : `Linha ${i + 1}`;
      return { chave: `linha-${i}`, rotulo, valor: k > 0 ? l.slice(k + 1).trim() : l };
    });
}

export async function pedirDescritivo(pedido: {
  descricao: string;
  tipo?: string | null;
  etiquetas: string[];
  estiloRotulo?: string;
  temReferencia?: boolean;
  descritivoDaReferencia?: string;
}): Promise<{ texto: string; campos: CampoDoDescritivo[] }> {
  const idToken = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
  const r = await fetch(`${SERVICO}/descritivo`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
    body: JSON.stringify({
      // `prompt` porque é o campo que a barreira de texto do servidor lê em toda requisição.
      prompt: pedido.descricao,
      tipo: pedido.tipo || "characters",
      tags: pedido.etiquetas,
      estiloRotulo: pedido.estiloRotulo,
      temReferencia: pedido.temReferencia,
      descritivoDaReferencia: pedido.descritivoDaReferencia,
    }),
  });
  const j = await r.json().catch(() => ({}));
  if (r.status === 422) throw new DescritivoRecusadoError(j?.message || "Esse pedido não pode virar imagem no HQ-Lab.");
  if (!r.ok || typeof j?.descritivo !== "string") throw new Error(j?.message || "Não deu para montar o descritivo agora.");
  const campos: CampoDoDescritivo[] = Array.isArray(j?.campos)
    ? j.campos.filter((c: CampoDoDescritivo) => c && typeof c.rotulo === "string").map((c: CampoDoDescritivo) => ({ chave: String(c.chave), rotulo: c.rotulo, valor: String(c.valor ?? "") }))
    : linhasParaCampos(j.descritivo);
  return { texto: j.descritivo, campos };
}

export type GrupoDeEtiqueta = "angulo" | "enquadramento" | "pose" | "outra";

export interface EtiquetaGlobal {
  id: string;
  texto: string;
  grupo: GrupoDeEtiqueta;
}

/** As etiquetas que a casa e as pessoas criaram — globais, para todo mundo logado. */
export async function listarEtiquetas(): Promise<EtiquetaGlobal[]> {
  try {
    const docs = await Firebase.getCollection("etiquetas", [], 200);
    return docs
      .filter((d) => typeof d.texto === "string")
      .map((d) => ({ id: String(d.id), texto: String(d.texto), grupo: (d.grupo as GrupoDeEtiqueta) ?? "outra" }))
      .sort((a, b) => a.texto.localeCompare(b.texto, "pt-BR"));
  } catch {
    return [];
  }
}

export class EtiquetaRecusadaError extends Error {}

/**
 * Cria uma etiqueta global. Qualquer pessoa logada pode (decisão do operador em 13/10); o texto
 * passa ANTES pela barreira de texto do servidor, e a regra do banco garante formato e autor.
 */
export async function criarEtiqueta(texto: string, grupo: GrupoDeEtiqueta, uid: string): Promise<EtiquetaGlobal> {
  const limpo = texto.replace(/\s+/g, " ").trim();
  if (limpo.length < 2 || limpo.length > 40) throw new EtiquetaRecusadaError("A etiqueta precisa ter de 2 a 40 letras.");
  const veredicto = await conferirTextos([limpo]);
  if (!veredicto.permitido) throw new EtiquetaRecusadaError(veredicto.recusados[0]?.mensagem || "Essa etiqueta não pode ser usada no HQ-Lab.");
  const ref = await Firebase.pushDocument("etiquetas", { texto: limpo, grupo, criadaPor: uid, criadaEm: Date.now() });
  return { id: (ref as { id?: string })?.id ?? limpo, texto: limpo, grupo };
}
